import { Component, ComponentInterface, Element, h, Host, JSX, Listen, Prop, State } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/workbench-global';
import { DEFAULT_WORKBENCH_THEME, WorkbenchTheme } from '../../shared/themes';
import { ViewportObserver } from '../../utils/viewport-observer';
import { getComputedBackgroundColor } from '../../shared/get-computed-background-color';

const DIRECTION_NEXT = 1;
const DIRECTION_PREVIOUS = -1;

/**
 * @slot - Contents of horizontal-scroll
 **/
@Component({
  tag: 'wb-horizontal-scroll',
  styleUrl: 'horizontal-scroll.scss',
  shadow: true,
})
export class HorizontalScrollComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** the colour scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme = DEFAULT_WORKBENCH_THEME;

  /** if present, hides the arrow navigation buttons */
  @Prop()
  public hideArrowNavigation: boolean = false;

  /** sets aria-label for the button that scrolls towards the end */
  @Prop()
  public scrollToEndButtonTitle: string = 'scroll to end';

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** sets aria-label for the button that scrolls towards the start */
  @Prop()
  public scrollToStartButtonTitle: string = 'scroll to start';

  @State()
  private isScrolledToStart: boolean = true;

  @State()
  private isScrolledToEnd: boolean = true;

  private observeStartPixel: ViewportObserver;
  private observeEndPixel: ViewportObserver;
  private scrollerEl: HTMLElement;
  private startPixelEl: HTMLElement;
  private endPixelEl: HTMLElement;

  private TagNames = transformTagNames(this, 'wb-horizontal-scroll', ['wb-icon', 'wb-round-button']);
  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  public connectedCallback(): void {
    this.updateDirection();
  }

  public componentWillLoad(): void {
    this.el.style.setProperty('--parent-background-color', getComputedBackgroundColor(this.el));
  }

  public componentDidLoad(): void {
    this.attachViewportObservers();
  }

  public disconnectedCallback(): void {
    this.detachViewportObservers();
  }

  private attachViewportObservers(): void {
    this.observeStartPixel = new ViewportObserver({
      element: this.startPixelEl,
      root: this.scrollerEl,
      // added to expand the box to the top so arrows/gradients are not visible when scrolled to the top or bottom of the screen
      rootMargin: '4000px 0px 4000px 0px',
      viewportEnterFn: () => (this.isScrolledToStart = true),
      viewportLeaveFn: () => (this.isScrolledToStart = false),
    });

    this.observeEndPixel = new ViewportObserver({
      element: this.endPixelEl,
      root: this.scrollerEl,
      rootMargin: '4000px 0px 4000px 0px',
      viewportEnterFn: () => (this.isScrolledToEnd = true),
      viewportLeaveFn: () => (this.isScrolledToEnd = false),
    });
  }

  private detachViewportObservers(): void {
    this.observeStartPixel?.disconnect();
    this.observeEndPixel?.disconnect();
  }

  private scrollToDirection(directionModifier: number): void {
    const rtlAdjustment = this.isRtl ? directionModifier : -directionModifier;
    const scrollLeft = this.scrollerEl.scrollLeft + (this.scrollerEl.clientWidth / 2) * rtlAdjustment;
    this.scrollerEl.scroll({ left: scrollLeft, behavior: 'smooth' });
  }

  private onScrollToEndClick = (): void => {
    this.scrollToDirection(DIRECTION_PREVIOUS);
  };

  private onScrollToStartClick = (): void => {
    this.scrollToDirection(DIRECTION_NEXT);
  };

  private bindScrollerEl = (el: HTMLElement): void => {
    this.scrollerEl = el;
  };

  private bindStartPixelEl = (el: HTMLElement): void => {
    this.startPixelEl = el;
  };

  private bindEndPixelEl = (el: HTMLElement): void => {
    this.endPixelEl = el;
  };

  public render(): JSX.Element {
    const { WbIcon, WbRoundButton } = this.TagNames;

    return (
      <Host class="wb-horizontal-scroll">
        <div
          class={{
            container: true,
            'has-start-gradient': !this.isScrolledToStart,
            'has-end-gradient': !this.isScrolledToEnd,
          }}
        >
          <div class="inner" ref={this.bindScrollerEl}>
            <div class="pixel is-start" ref={this.bindStartPixelEl} />
            <slot />
            <div class="pixel is-end" ref={this.bindEndPixelEl} />
          </div>
          {!this.hideArrowNavigation && !this.isScrolledToStart && (
            <WbRoundButton size="new-s" theme={this.theme} class="arrow is-start" onClick={this.onScrollToStartClick}>
              <WbIcon name="chevron-left/24" flip-rtl />
              {this.scrollToStartButtonTitle}
            </WbRoundButton>
          )}
          {!this.hideArrowNavigation && !this.isScrolledToEnd && (
            <WbRoundButton size="new-s" theme={this.theme} class="arrow is-end" onClick={this.onScrollToEndClick}>
              <WbIcon name="chevron-right/24" flip-rtl />
              {this.scrollToEndButtonTitle}
            </WbRoundButton>
          )}
        </div>
      </Host>
    );
  }
}
